import { InputAdornment, InputBaseComponentProps, TextField as MUITextField } from '@mui/material';
import React, { forwardRef, memo } from 'react';
import { NumericFormat, NumericFormatProps } from 'react-number-format';
import { GMIconButton, GMInfoIcon } from '../../../Buttons/IconButton';
import { GMOutlinedInputLabel } from '../../InputLabel';
import { GMInputWrapper } from '../../InputWrapper';
import css from './TextField.module.scss';
import { ErrorLevel } from '../../../../API/Enums';
import { GoMotoComponent } from '../../../../API/GoMoto.types';
import { NumericMaskProps, TextFieldProps } from './TextField.types';
import { AppType, useAppCoreState } from '../../../../Contexts/AppCore';

const TextField: GoMotoComponent<TextFieldProps> = memo(({
    name,
    value,
    label,
    type,
    autofocus,
    onChange,
    onBlur,
    onFocus,
    helperText,
    startAdornment,
    endAdornment,
    endAdornmentText,
    placeholder,
    numericOnly,
    isCurrency,
    shrinkLabel,
    noMaxLength,
    numericMaxValue,
    onChangeRegexTest,
    multiline,
    multilineRows = 3,
    maxLength = 255,
    autoComplete = 'on',
    readonly,
    id,
    centerInputValue = false,
    showCharacterCount = false,
    ...wrapperProps
}) => {

    const { RunningAppType } = useAppCoreState();
    const displayCharacterCount = multiline && (RunningAppType === AppType.Dashboard || showCharacterCount);
    // Hook-form fields reset from an API response can hold null for never-saved text.
    const displayValue: string = value ?? '';

    const setCursorPosition = (event: React.ChangeEvent<HTMLInputElement>, caretPos: number) => {
        const scrollLeft: number = event.target.scrollLeft;

        window.requestAnimationFrame(() => {
            event.target.selectionStart = caretPos;
            event.target.selectionEnd = caretPos;
            event.target.scrollLeft = scrollLeft;
        });
    };

    const inputProps: InputBaseComponentProps = {
        maxLength: noMaxLength ? undefined : maxLength,
        size: 1,
        readOnly: readonly,
        'is-currency': isCurrency,
        className: centerInputValue ? css.centeredFieldValue : undefined,
    };

    if (numericOnly && numericMaxValue !== undefined) {
        inputProps.maxValue = numericMaxValue;
    }

    return (
        <GMInputWrapper {...wrapperProps}>
            {
                wrapperProps.variant === 'outlined' ?
                    <GMOutlinedInputLabel
                        label={label}
                        required={wrapperProps.required}
                        error={wrapperProps.error}
                        disabled={wrapperProps.disabled}
                        color={wrapperProps.color}
                    /> : null
            }
            <MUITextField
                id={id}
                autoComplete={autoComplete}
                variant={wrapperProps.variant}
                type={type}
                name={name}
                label={wrapperProps.variant === 'standard' ? label : ''}
                value={displayValue}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    if (event.target.value) {
                        if (onChangeRegexTest && !onChangeRegexTest.test(event.target.value)) {
                            setCursorPosition(event, (event.target.selectionEnd ?? 0) - 1);
                            return;
                        }
                    }

                    onChange && onChange(event);
                }}
                onBlur={onBlur}
                onFocus={onFocus}
                disabled={wrapperProps.disabled}
                autoFocus={autofocus}
                inputProps={inputProps}
                multiline={multiline}
                rows={multiline ? multilineRows : undefined}
                helperText={helperText}
                InputLabelProps={
                    shrinkLabel ? { shrink: true } :
                        readonly && !value ? { shrink: false } :
                            undefined
                }
                InputProps={{
                    startAdornment: startAdornment,
                    endAdornment: (
                        <>
                            {endAdornmentText ? (
                                <InputAdornment
                                    position={'end'}
                                    sx={{ paddingRight: '10px' }}
                                >
                                    {endAdornmentText}
                                </InputAdornment>
                            ) : null}
                            {!endAdornment ? null : endAdornment.onClick ? (
                                <GMIconButton
                                    color={endAdornment.color ?? wrapperProps.color}
                                    tooltip={endAdornment.tooltip}
                                    notTabbable
                                    disabled={endAdornment.disabled ?? wrapperProps.disabled}
                                    onClick={endAdornment.onClick}
                                >
                                    {endAdornment.icon}
                                </GMIconButton>
                            ) : (
                                <GMInfoIcon
                                    tooltip={endAdornment.tooltip}
                                    color={endAdornment.color ?? wrapperProps.color}
                                />
                            )}
                        </>
                    ),
                    inputComponent: numericOnly ? NumericMask : multiline ? 'textarea' : undefined,
                }}
                placeholder={placeholder}
                error={wrapperProps.error !== undefined && wrapperProps.error !== ErrorLevel.None}
                required={wrapperProps.required}
            />
            {displayCharacterCount ? (
                <div className={css.characterCount}>
                    {`${displayValue.length}/${maxLength}`}
                </div>
            ) : null}
        </GMInputWrapper >
    );
});

export default TextField;

const NumericMask = forwardRef<NumericFormatProps, NumericMaskProps>(({ onChange, maxLength, maxValue, 'is-currency': isCurrency, suffix, ...props }, ref) => {
    return (
        <NumericFormat
            {...props}
            suffix={suffix ? ` ${suffix}` : undefined}
            allowNegative={false}
            decimalScale={isCurrency ? 2 : 0}
            allowLeadingZeros={true}
            fixedDecimalScale={isCurrency}
            maxLength={maxLength}
            isAllowed={(values) => {
                if (maxValue !== undefined && values.value !== '' && +values.value > maxValue) {
                    return false;
                }

                return true;
            }}
            getInputRef={ref}
            onValueChange={(values) => {
                onChange({ target: { value: values.value } });
            }}
        />
    );
});

